import { useState } from "react";
import { Check, Loader2, Pencil, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, pollJob } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { SECTIONS, SectionEditor, SectionView } from "./Sections";

function SectionCard({ k, label, kind, value, locked, onChange, campaignId }) {
  const { path } = useWorkspace();
  const [editing, setEditing] = useState(false);
  const [regen, setRegen] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(path(`/campaigns/${campaignId}/strategy/regenerate`), { section: k, guidance });
      const res = await pollJob(path(`/jobs/${data.job_id}`));
      setProposal(res.value); setRegen(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  return (
    <section data-testid={`strategy-section-${k}`} className="cq-card p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="cq-muted-label !text-[#AAB6CC]">{label}</h3>
        {!locked && (
          <div className="flex items-center gap-1">
            <button data-testid={`strategy-regenerate-${k}`} onClick={() => setRegen(!regen)} disabled={busy} className="cq-btn cq-btn-ghost !py-1 !px-2.5 !text-xs"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Regenerate</button>
            <button data-testid={`strategy-edit-toggle-${k}`} onClick={() => setEditing(!editing)} className="cq-btn cq-btn-ghost !py-1 !px-2.5 !text-xs">{editing ? <><Check size={12} /> Done</> : <><Pencil size={12} /> Edit</>}</button>
          </div>
        )}
      </div>
      {regen && !proposal && (
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <input data-testid={`strategy-regenerate-guidance-${k}`} className="cq-input !py-2" placeholder="Optional guidance, e.g. 'more specific to accountants'" value={guidance} onChange={(e) => setGuidance(e.target.value)} />
          <button data-testid={`strategy-regenerate-run-${k}`} onClick={regenerate} disabled={busy} className="cq-btn cq-btn-primary">{busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {busy ? "Regenerating" : "Run"}</button>
        </div>
      )}
      {proposal !== null && (
        <div data-testid={`strategy-proposal-${k}`} className="mb-4 p-4 rounded-lg border border-[#22E6F2]/30 bg-[#22E6F2]/5">
          <div className="cq-label mb-3">Proposed replacement · review before accepting</div>
          <SectionView kind={kind} value={proposal} />
          <div className="mt-4 flex gap-2">
            <button data-testid={`strategy-proposal-accept-${k}`} onClick={() => { onChange(proposal); setProposal(null); toast.success("Section replaced. Save the draft to keep it."); }} className="cq-btn cq-btn-success !py-1.5"><Check size={13} /> Accept</button>
            <button data-testid={`strategy-proposal-discard-${k}`} onClick={() => setProposal(null)} className="cq-btn cq-btn-ghost !py-1.5"><X size={13} /> Discard</button>
          </div>
        </div>
      )}
      {editing && !locked ? <SectionEditor k={k} kind={kind} value={value} onChange={onChange} /> : <SectionView kind={kind} value={value} />}
    </section>
  );
}

export default function StrategyEditor({ strategy, locked, onChange, campaignId }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="strategy-editor">
      {SECTIONS.map(([k, label, kind]) => (
        <div key={k} className={kind === "list" && k !== "messaging_pillars" ? "" : "xl:col-span-2"}>
          <SectionCard k={k} label={label} kind={kind} value={strategy?.[k]} locked={locked} campaignId={campaignId}
            onChange={(v) => onChange({ ...strategy, [k]: v })} />
        </div>
      ))}
    </div>
  );
}
